import { forwardRef, useCallback } from "react";
import { Control, Controller, FieldValues, Path, RegisterOptions } from "react-hook-form";
import styled from "@emotion/styled";

import { combineRefs } from "./util";

export type FontStyle = "h1" | "h2" | "h3" | "body" | "description";

const FONT_STYLE_MAP: Record<
  FontStyle,
  { fontSize: number; fontWeight: number; lineHeight: string }
> = {
  h1: { fontSize: 24, fontWeight: 600, lineHeight: "133%" },
  h2: { fontSize: 20, fontWeight: 600, lineHeight: "140%" },
  h3: { fontSize: 16, fontWeight: 600, lineHeight: "150%" },
  body: { fontSize: 14, fontWeight: 400, lineHeight: "157%" },
  description: { fontSize: 12, fontWeight: 400, lineHeight: "166%" },
};

export type TextAreaInputProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  React.ComponentPropsWithoutRef<"textarea">,
  "style"
> & {
  name: Path<TFieldValues>;
  control?: Control<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  fontStyle?: FontStyle;
  minRows?: number;
  borderless?: boolean;
};

function resize(textarea: HTMLTextAreaElement) {
  // Needed to reset height to auto to allow shrinking when deleting text
  textarea.style.height = "auto";
  textarea.style.height = `${textarea.scrollHeight}px`;
}

/**
 * Auto-resizing textarea with configurable font styling and optional React Hook Form integration.
 * Grows and shrinks with content automatically.
 *
 * @param fontStyle - Typography variant: "h1" | "h2" | "h3" | "body" | "description" (default: "body")
 * @param minRows - Minimum number of visible rows (default: 1)
 * @param control - React Hook Form control object for form integration
 * @param name - Field name for React Hook Form
 */
const TextAreaInput = forwardRef(function TextAreaInput<
  TFieldValues extends FieldValues = FieldValues,
>(
  {
    control,
    name,
    fontStyle = "body",
    minRows = 1,
    onInput,
    onBlur,
    rules,
    ...props
  }: TextAreaInputProps<TFieldValues>,
  externalRef: React.ForwardedRef<HTMLTextAreaElement>,
) {
  const setRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      combineRefs(externalRef)(node);

      if (node) {
        resize(node);
      }
    },
    [externalRef],
  );

  const handleInput = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      resize(e.currentTarget);
      onInput?.(e);
    },
    [onInput],
  );

  const sharedProps = {
    autoComplete: "off", // By default disable autoComplete
    ...props,
    fontStyle,
    rows: minRows,
    onInput: handleInput,
  };

  if (control) {
    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <StyledTextArea
            {...sharedProps}
            {...field}
            onBlur={(e) => {
              field.onBlur();
              onBlur?.(e);
            }}
            ref={combineRefs(field.ref, setRef)}
          />
        )}
      />
    );
  }

  return <StyledTextArea {...sharedProps} name={name} ref={setRef} />;
}) as <TFieldValues extends FieldValues = FieldValues>(
  props: TextAreaInputProps<TFieldValues> & { ref?: React.ForwardedRef<HTMLTextAreaElement> },
) => React.ReactElement;

const StyledTextArea = styled.textarea<{ fontStyle: FontStyle; borderless?: boolean }>(
  ({ theme, fontStyle, borderless }) => {
    const { fontSize, fontWeight, lineHeight } = FONT_STYLE_MAP[fontStyle];

    return {
      width: "100%",
      minWidth: 0,
      maxWidth: "100%",
      borderRadius: 4,
      border: borderless ? "none" : `1px solid ${theme.colors.sysBorderDecorative}`,
      backgroundColor: theme.colors.sysBackgroundDefault,
      padding: borderless ? "5px 0px" : "5px 8px",
      fontSize,
      fontFamily: theme.font.fontFamily,
      fontWeight,
      lineHeight,
      color: theme.colors.sysTextDefault,
      outline: "none",
      resize: "none",
      overflow: "hidden",
      transition: "border-color 0.2s, background-color 0.2s, color 0.2s",
      boxSizing: "border-box",

      "&::placeholder": {
        color: theme.colors.sysTextInactive,
      },

      "&:hover:not(:disabled):not(:user-invalid)": {
        borderColor: theme.colors.sysPrimaryDefault,
      },

      "&:focus": {
        borderColor: theme.colors.sysPrimaryDefault,
        outline: `2px solid ${theme.colors.sysPrimaryDefault}1A`,
      },

      "&:user-invalid": {
        borderColor: theme.colors.sysDestructiveDefault,

        "&:focus": {
          borderColor: theme.colors.sysDestructiveDefault,
          outline: `2px solid ${theme.colors.sysDestructiveDefault}1A`,
        },
      },

      "&:disabled": {
        backgroundColor: theme.colors.sysBackgroundInactive,
        color: theme.colors.sysTextInactive,
        borderColor: theme.colors.sysBackgroundInactive,
        cursor: "not-allowed",
      },
    };
  },
);

StyledTextArea.displayName = "StyledTextArea";

export default TextAreaInput;
